import { PricingDetails, multiLanguage } from "@db-models/pricing-db.model";

// Stripe-imported per-currency monthly prices for v2.
// Yearly = monthly * 12 (no discount per business decision).
const v2Price = (monthly: number) => ({
  monthly: { original_price: monthly },
  yearly: { original_price: monthly, saving_amount: 0, saving_percent: 0 },
});

// v2.4 — graduated card length (Basic 10 / Starter 13 / Team 16 / Enterprise 20)
// with workflow quotas surfaced per tier (10 / 20 / 50 / 100). Quota numbers are
// display-only strings pending leadership sign-off on whether to add a typed
// `included_workflows` field with enforcement.
// Asymmetry is intentional: card length is itself a value signal on a
// pricing page. Higher-tier features visibly outnumber lower tiers so the
// step-up justifies the price jump.
// Same category order across tiers: Capacity → Booking & Calendar → Workflows →
// Branding → Payments → Integrations → Reporting → Security → Support.
//
// Phrase-level emphasis: wrap quantities and headline keywords in <strong>
// (rendered via [innerHTML] + .pricing-card-feature-text strong styling) so
// the eye lands on what differentiates each tier — "1 Resource", "10 Workflows",
// "Online Payments", "CRM Integration", "100 Workflows", etc. Applied in both
// dashboard and sales modes; no per-bullet boolean flag needed.

const featureBasic: multiLanguage[] = [
  { en_US: "<strong>1 Resource</strong> · 1 Branch", de_CH: "<strong>1 Ressource</strong> · 1 Filiale" },
  { en_US: "<strong>1 Group Appointment</strong> included", de_CH: "<strong>1 Gruppentermin</strong> inklusive" },
  { en_US: "<strong>Unlimited Bookings</strong>", de_CH: "<strong>Unbegrenzte Buchungen</strong>" },
  { en_US: "Calendar Sync (Google · iCloud)", de_CH: "Kalendersynchronisation (Google · iCloud)" },
  { en_US: "Booking Page & Embeddable Widget", de_CH: "Buchungsseite & einbettbares Widget" },
  { en_US: "Email Confirmations & Reminders", de_CH: "E-Mail-Bestätigungen & Erinnerungen" },
  { en_US: "<strong>10 Workflows</strong> for Auto Reminders & Follow-ups", de_CH: "<strong>10 Workflows</strong> für automatische Erinnerungen & Nachfassaktionen" },
  { en_US: "Custom Forms", de_CH: "Individuelle Formulare" },
  { en_US: "Zoom Video Consultations", de_CH: "Zoom-Videoberatungen" },
  { en_US: "GDPR & nDSG Compliant · Swiss-hosted", de_CH: "DSGVO- & nDSG-konform · in der Schweiz gehostet" },
  { en_US: "Self-Service Support", de_CH: "Self-Service-Support" },
];

const featureStarter: multiLanguage[] = [
  { en_US: "Up to <strong>5 Resources</strong> · <strong>1 Branch</strong>", de_CH: "Bis zu <strong>5 Ressourcen</strong> · <strong>1 Filiale</strong>" },
  { en_US: "5 Group Appointments included", de_CH: "5 Gruppentermine inklusive" },
  { en_US: "<strong>2-Way Calendar Sync</strong> (Google · iCloud · CalDAV · Office 365)", de_CH: "<strong>2-Wege Kalendersynchronisation</strong> (Google · iCloud · CalDAV · Office 365)" },
  { en_US: "Round Robin Booking Distribution", de_CH: "Round Robin Buchungsverteilung" },
  { en_US: "<strong>Video Consultations</strong> via GoTo Meeting · Webex", de_CH: "<strong>Videoberatungen</strong> via GoTo Meeting · Webex" },
  { en_US: "Custom Logo", de_CH: "Benutzerdefiniertes Logo" },
  { en_US: "Branches & Team Structure", de_CH: "Filialen- & Team-Struktur" },
  { en_US: "<strong>Online Payments</strong> via Stripe", de_CH: "<strong>Online-Zahlungen</strong> via Stripe" },
  { en_US: "Booking Cancellation & Rescheduling Rules", de_CH: "Stornierungs- & Umbuchungsregeln" },
  { en_US: "<strong>20 Workflows</strong> for Automation", de_CH: "<strong>20 Workflows</strong> für Automatisierung" },
  { en_US: "1 Webhook Endpoint", de_CH: "1 Webhook-Endpunkt" },
  { en_US: "Standard Email Support (~72h)", de_CH: "Standard E-Mail-Support (~72h)" },
];

const featureTeam: multiLanguage[] = [
  { en_US: "Up to <strong>15 Resources</strong> · Up to <strong>5 Branches</strong>", de_CH: "Bis zu <strong>15 Ressourcen</strong> · Bis zu <strong>5 Filialen</strong>" },
  { en_US: "15 Group + 5 Conference Appointments included", de_CH: "15 Gruppen- + 5 Konferenztermine inklusive" },
  { en_US: "Multilingual Booking", de_CH: "Mehrsprachige Buchung" },
  { en_US: "<strong>Microsoft Teams</strong> Video Consultations", de_CH: "<strong>Microsoft Teams</strong> Videoberatungen" },
  { en_US: "<strong>Corporate Branding</strong> (Colours · Custom Domain · Email Templates)", de_CH: "<strong>Corporate Branding</strong> (Farben · Eigene Domain · E-Mail-Vorlagen)" },
  { en_US: "Own SMTP Mail Server", de_CH: "Eigener SMTP-Mailserver" },
  { en_US: "Qualification Questions (Routing)", de_CH: "Qualifizierungsfragen (Routing)" },
  { en_US: "<strong>50 Workflows</strong> + Multi-step Automation", de_CH: "<strong>50 Workflows</strong> + mehrstufige Automatisierung" },
  { en_US: "<strong>CRM Integration</strong>", de_CH: "<strong>CRM-Integration</strong>" },
  { en_US: "5 Webhook Endpoints", de_CH: "5 Webhook-Endpunkte" },
  { en_US: "Online Payments via SaferPay (Klarna · TWINT · PostFinance)", de_CH: "Online-Zahlungen via SaferPay (Klarna · TWINT · PostFinance)" },
  { en_US: "Bexio Invoice Integration", de_CH: "Bexio-Rechnungsintegration" },
  { en_US: "SMS Notifications", de_CH: "SMS-Benachrichtigungen" },
  { en_US: "Reporting & Data Exports", de_CH: "Reporting & Datenexporte" },
  { en_US: "Encrypted Customer & Appointment Data", de_CH: "Verschlüsselte Kunden- & Termindaten" },
  { en_US: "Priority Email Support + Callback (~24h)", de_CH: "Priorisierter E-Mail-Support + Rückruf (~24h)" },
];

const featureEnterprise: multiLanguage[] = [
  { en_US: "<strong>Volume-discounted Resources & Branches</strong>", de_CH: "<strong>Mengenrabatt für Ressourcen & Filialen</strong>" },
  { en_US: "30 Group + 10 Conference Appointments included", de_CH: "30 Gruppen- + 10 Konferenztermine inklusive" },
  { en_US: "<strong>100 Workflows</strong> + Full Automation Engine", de_CH: "<strong>100 Workflows</strong> + vollständige Automatisierungs-Engine" },
  { en_US: "Exchange On-Premise · Impersonation · Office 365 Enterprise", de_CH: "Exchange On-Premise · Impersonation · Office 365 Enterprise" },
  { en_US: "Dynamic Availabilities (Office 365 / Microsoft Exchange Calendar)", de_CH: "Dynamische Verfügbarkeiten (Office 365 / Microsoft Exchange Kalender)" },
  { en_US: "<strong>White-Label</strong>", de_CH: "<strong>White-Label</strong>" },
  { en_US: "Custom Booking Process", de_CH: "Individueller Buchungsprozess" },
  { en_US: "<strong>REST API Access</strong>", de_CH: "<strong>REST-API-Zugang</strong>" },
  { en_US: "CRM/ERP Integrations (Salesforce · Dynamics · SAP)", de_CH: "CRM/ERP-Integrationen (Salesforce · Dynamics · SAP)" },
  { en_US: "<strong>SSO</strong> (Azure AD · Entra ID · LDAP)", de_CH: "<strong>SSO</strong> (Azure AD · Entra ID · LDAP)" },
  { en_US: "Custom Roles & Permissions", de_CH: "Individuelle Rollen & Berechtigungen" },
  { en_US: "<strong>Audit Logs</strong> (30 days retention)", de_CH: "<strong>Audit-Logs</strong> (30 Tage Aufbewahrung)" },
  { en_US: "Advanced Security Settings", de_CH: "Erweiterte Sicherheitseinstellungen" },
  { en_US: "Compliance with Audit Protocols", de_CH: "Compliance mit Audit-Protokollen" },
  { en_US: "Video Consultations via Unblu", de_CH: "Videoberatungen via Unblu" },
  { en_US: "Dedicated Test Environment", de_CH: "Dediziertes Test-System" },
  { en_US: "Custom On-boarding & Training", de_CH: "Individuelles On-boarding & Schulung" },
  { en_US: "Dedicated Infrastructure", de_CH: "Dedizierte Infrastruktur" },
  { en_US: "<strong>Personal Account Manager</strong> (SLA-priority Support)", de_CH: "<strong>Persönlicher Account-Manager</strong> (SLA-priorisierter Support)" },
];

// Same feature list is reused for dashboard + sales modes — both surfaces show
// identical tier content; only the surrounding chrome differs (token gating,
// current-subscription highlighting). User will reposition sections per mode later.
const reusedFeatureList = (features: multiLanguage[]) => ({
  dashboard: features,
  sales: features,
});

export const V2_PRICING_JSON: PricingDetails[] = [
  {
    chf: v2Price(35),
    eur: v2Price(35),
    usd: v2Price(39),
    gbp: v2Price(29),
    description: "Basic",
    id: 202,
    included_bookings: 0,
    name: "basic",
    min_workers: 1,
    min_store: 1,
    min_group_appointment: 1,
    min_conference: 0,
    subscription_title: "Basic",
    subscription_subtitle: {
      en_US: "For solo professionals. Get online booking up and running in minutes.",
      de_CH: "Für Solo-Selbstständige. Online-Terminbuchung in wenigen Minuten startklar.",
    },
    subscription_subtitle_sales: {
      en_US: "The perfect starting point for GDPR-compliant online appointment scheduling. Calendar sync, automated reminders and an embeddable widget — go live today.",
      de_CH: "Der perfekte Ausgangspunkt für DSGVO-konforme Online-Terminbuchung. Kalendersynchronisation, automatische Erinnerungen und ein einbettbares Widget — heute live gehen.",
    },
    feature_list_title: {
      en_US: "Includes",
      de_CH: "Enthält",
    },
    feature_list: reusedFeatureList(featureBasic),
  },
  {
    chf: v2Price(249),
    eur: v2Price(269),
    usd: v2Price(299),
    gbp: v2Price(229),
    description: "Starter",
    id: 203,
    included_bookings: 0,
    name: "starter",
    min_workers: 5,
    min_store: 1,
    min_group_appointment: 5,
    min_conference: 0,
    subscription_title: "Starter",
    subscription_subtitle: {
      en_US: "For small teams up to 5. Sync calendars across your team and add your branding.",
      de_CH: "Für kleine Teams bis 5 Personen. Kalender im Team synchronisieren und eigenes Branding ergänzen.",
    },
    subscription_subtitle_sales: {
      en_US: "Take payments, run video consultations and bring your brand to every booking. Stripe checkout, Microsoft Exchange sync and 2-way calendar integration.",
      de_CH: "Zahlungen entgegennehmen, Videoberatungen durchführen und das eigene Branding auf jede Buchung übertragen. Stripe-Checkout, Microsoft Exchange Sync und 2-Wege Kalenderintegration.",
    },
    feature_list_title: {
      en_US: "Everything in Basic, plus",
      de_CH: "Alles in Basic, plus",
    },
    feature_list: reusedFeatureList(featureStarter),
  },
  {
    chf: v2Price(479),
    eur: v2Price(519),
    usd: v2Price(599),
    gbp: v2Price(449),
    description: "Team",
    id: 204,
    included_bookings: 0,
    name: "team",
    min_workers: 15,
    min_store: 5,
    min_group_appointment: 15,
    min_conference: 5,
    subscription_title: "Team",
    subscription_subtitle: {
      en_US: "For mid-sized companies up to 15. Multi-location operations with workflows, branding, and reporting.",
      de_CH: "Für mittelgrosse Unternehmen bis 15 Personen. Mehrere Standorte mit Workflows, Branding und Reporting.",
    },
    subscription_subtitle_sales: {
      en_US: "Multi-location operations with the full automation engine, CRM sync and corporate branding. Built for businesses scaling across multiple branches.",
      de_CH: "Multi-Standort-Betrieb mit voller Automatisierungs-Engine, CRM-Synchronisation und Corporate Branding. Für Unternehmen, die über mehrere Filialen skalieren.",
    },
    feature_list_title: {
      en_US: "Everything in Starter, plus",
      de_CH: "Alles in Starter, plus",
    },
    feature_list: reusedFeatureList(featureTeam),
  },
  {
    chf: { yearly: { original_price: 0 }, monthly: { original_price: 0 } },
    eur: { yearly: { original_price: 0 }, monthly: { original_price: 0 } },
    gbp: { yearly: { original_price: 0 }, monthly: { original_price: 0 } },
    usd: { yearly: { original_price: 0 }, monthly: { original_price: 0 } },
    description: "Enterprise",
    id: 205,
    included_bookings: 0,
    name: "enterprise",
    min_workers: 25,
    min_store: 10,
    min_group_appointment: 30,
    min_conference: 10,
    subscription_title: "Enterprise",
    subscription_subtitle: {
      en_US: "For large organisations. Bespoke deployment with SSO, API, and SLA-backed support.",
      de_CH: "Für grosse Organisationen. Massgeschneiderte Implementierung mit SSO, API und SLA-Support.",
    },
    subscription_subtitle_sales: {
      en_US: "Bespoke deployment with SSO, audit logs, dedicated infrastructure and a personal account manager backed by an SLA. Built for regulated industries.",
      de_CH: "Massgeschneiderte Implementierung mit SSO, Audit-Logs, dedizierter Infrastruktur und persönlichem Account-Manager nach SLA. Für regulierte Branchen entwickelt.",
    },
    feature_list_title: {
      en_US: "Everything in Team, plus",
      de_CH: "Alles in Team, plus",
    },
    feature_list: reusedFeatureList(featureEnterprise),
  },
];
